import { httpPc } from '@/utils/httpPc';

// AI报表信息接口类型定义
export interface AiReportInfo {
  enabledAiDataAnalytics: boolean;
  company_id: number;
  accounts_id: number;
  industry_id: number;
  industry_name: string;
  accounts_name: string;
  version: string;
}

export interface ReportMenuItem {
  group_id: string;
  group_name: string;
  icon: string;
  key: string;
  name: string;
}

/**
 * 获取AI报表信息
 * @returns Promise<AiReportInfo | null>
 */
export const getAiReportInfo = async (): Promise<AiReportInfo | null> => {
  try {
    const response = await httpPc.get<AxiosHttpPromise<AiReportInfo>>('/Manager/Accounts/aiReportInfo');
    if (response.data.code === 200) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error('获取AI报表信息失败:', error);
    return null;
  }
};

/**
 * 获取支持的报表菜单列表
 * @param dataSource 数据源，默认为2
 * @returns Promise<ReportMenuItem[]>
 */
export const getSupportedReports = async (dataSource: number = 2): Promise<ReportMenuItem[]> => {
  try {
    const response = await httpPc.get<AxiosHttpPromise<ReportMenuItem[]>>(`/Manager/Report/quickBiReportMenu?data_source=${dataSource}`);
    if (response.data.status === 'T') {
      return response.data.data;
    }
    return [];
  } catch (error) {
    console.error('获取支持的报表菜单失败:', error);
    return [];
  }
};

/**
 * 检查是否应该显示AI按钮
 * @param aiInfo AI报表信息
 * @param supportedReports 支持的报表列表
 * @param currentReportName 当前报表名称
 * @returns boolean
 */
export const shouldShowAiButton = (
  aiInfo: AiReportInfo | null,
  supportedReports: ReportMenuItem[],
  currentReportName: string
): boolean => {
  if (!aiInfo) {
    return false;
  }

  // 如果 version 不为 free 或 standard，默认显示AI按钮
  if (['free', 'standard'].includes(aiInfo.version)) {
    return false;
  }
  
  return supportedReports.some(report => report.key === currentReportName);
};

/**
 * 获取AI报表配置信息（组合接口）
 * @param reportName 报表名称
 * @returns Promise<{aiInfo: AiReportInfo | null, supportedReports: ReportMenuItem[], showAiButton: boolean}>
 */
export const getAiReportConfig = async (reportName: string) => {
  const [aiInfo, supportedReports] = await Promise.all([
    getAiReportInfo(),
    getSupportedReports()
  ]);
  const showAiButton = shouldShowAiButton(aiInfo, supportedReports, reportName);
  
  return {
    aiInfo,
    supportedReports,
    showAiButton
  };
};